import React, { useCallback, useState } from 'react';
import { Div } from '@pinpoint/design-kit';
import { useDispatch } from 'react-redux';

import { ModelProp } from 'src/store/typings';
import { noOp } from 'src/functions';
import { FairnessClassDropdown } from 'src/components/dropdown';
import CONSTANTS from 'src/store/constants';
import { appActions, modelViewActions } from 'src/store/actions';

import { Toggles, MinAIRInput, MaxAIRInput } from './interactables';
import { AirButtonGroup, RemediateButton } from './buttons';
import Infographic from './infographic';

export default function ActuarialMode({ model }) {
  const dispatch = useDispatch();
  // State
  const [airToggle, setAirToggle] = useState(false);
  const [pricingToggle, setPricingToggle] = useState(false);
  const [demoSelection, setDemoSelection] = useState(0);
  const [classIndex, setClassIndex] = useState(0);
  const [minVal, setMinVal] = useState(0.83);
  const [maxVal, setMaxVal] = useState(1.2);
  const hasPricing = !!model?.modelData?.dataPayload?.fairness?.dimensions?.find?.(
    (val) => val === 'Actual Pricing AIR'
  );

  // Handlers
  const toggleAir = useCallback(() => {
    setAirToggle(!airToggle);
  }, [airToggle]);
  const togglePricing = useCallback(() => setPricingToggle(!pricingToggle), [pricingToggle]);
  const processInput = useCallback(
    (evt) => {
      evt.preventDefault();
      const { target } = evt;
      const { value: trial, id: iptId } = target;
      if (iptId === 'max-val') setMaxVal(+trial);
      if (iptId === 'min-val') setMinVal(+trial >= maxVal ? maxVal - 0.01 : +trial);
    },
    [maxVal]
  );
  const handleRemediateTrigger = useCallback(() => {
    dispatch(
      modelViewActions.remediateModel(model.id, CONSTANTS.TAL_MODEL.FAIRNESS_CLASSES[classIndex], minVal, maxVal)
    );
    dispatch(appActions.enqueueNotification('Starting remediation of model.', { autoClose: true, timeout: 5000 }));
  }, [classIndex, dispatch, maxVal, minVal, model.id]);

  // Effects

  return (
    <Div
      bg="white"
      rounded="md"
      overflow="hidden"
      d="flex"
      flexDir="column"
      data-test="paper"
      minH="0px"
      h="100%"
      maxH="100%"
      minW="0px"
      w="100%"
      maxW="100%"
      p={{ t: '2rem', b: airToggle ? 'calc(4rem + 2px)' : '0', x: '2rem' }}
      textSize="input"
      textWeight="500"
    >
      <Toggles
        airToggle={airToggle}
        toggleAir={toggleAir}
        statToggle={null}
        toggleStat={noOp}
        hasPricing={hasPricing}
        pricingToggle={pricingToggle}
        togglePricing={togglePricing}
      />
      <AirButtonGroup selected={demoSelection} handleSelection={setDemoSelection} />
      <Div flexGrow="1" d="grid">
        <Infographic
          model={model}
          showPricing={pricingToggle}
          showValue={airToggle}
          demoIndex={demoSelection}
          minVal={minVal}
          maxVal={maxVal}
        />
      </Div>
      {!airToggle && (
        <Div textColor="shade33" m={{ y: '.5rem' }} d="flex" align="center" justify="center">
          <Div tag="span">Ensure that the AIRs for</Div>
          <Div m="0.5rem" w="8rem">
            <FairnessClassDropdown onSelection={setClassIndex} classIndex={classIndex} />
          </Div>
          remain between
          <MinAIRInput value={minVal} onChange={processInput} />
          and
          <MaxAIRInput value={maxVal} onChange={processInput} />
          <RemediateButton disabled={airToggle} onClick={handleRemediateTrigger} />
        </Div>
      )}
    </Div>
  );
}

ActuarialMode.propTypes = {
  model: ModelProp.isRequired,
};
